import type { FC, JSX } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ServiceProviderReceiptBatchRenderRowProps } from './ServiceProviderReceiptBatchReceiptsView';
import { ServiceProviderReceiptBatchReceiptsView } from './ServiceProviderReceiptBatchReceiptsView';
import type { FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { Formik, useFormikContext } from 'formik';
import { Card, Stack } from '@mui/material';
import { ReceiptBatchActionButtons } from './ReceiptBatchActionButtons';
import * as Yup from 'yup';
import { endOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { EnlargeImageGallery } from '@pflegenavi/transaction-components';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { LoadingContainer } from '@pflegenavi/web-components';
import { useFormatting } from '@pflegenavi/frontend/localization';
import type { Gender } from '@pflegenavi/shared/api';

import isEqual from 'lodash.isequal';
import { ServiceProviderReceiptBatchCommonContainer } from './ServiceProviderReceiptBatchCommonContainer';
import {
  getMaxReceiptBatchAmountInCents,
  MAX_TRANSACTION_AMOUNT_IN_CENTS,
} from '@pflegenavi/shared/constants';
import { TestIds } from '../testIds';
import { ServiceProviderReceiptBatchQuickResidentSelection } from './ResidentQuickSelection/ServiceProviderReceiptBatchQuickResidentSelection';
import { updateReceiptBatchWithMergeBase } from './utils/useUpdateReceiptBatchWithMerge';
import { useSnackbar } from 'notistack';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useSelectedNursingHomePaymentProcessor } from '../useSelectedNursingHomePaymentProcessor';
import type { ReceiptBatchFormValues as FormikReceiptBatchFormValues } from '../interfaces/ReceiptBatchFormValues';
import { useShallow } from 'zustand/react/shallow';

export interface ReceiptBatchFormValues {
  nursingHomeId?: string;
  receiptDate?: Date;
  batchImageIds?: string[];
  individualDatesMode: boolean;
  generateReceipts: boolean;
  receipts: BatchReceiptDetails[];
}

export interface TaxItem {
  taxAmount: number;
  taxAmountInPercentage: number;
  taxName: string;
  countryId: string;
  explanationShort?: string | undefined;
  explanationLong?: string | undefined;
  displayExplanation: boolean;
  hidePercentage?: boolean;
  paragraph?: string | undefined;
  taxAbbreviation?: string | undefined;
}

export interface LineItem {
  id: string | undefined;
  _internalId: string;
  amount: number | undefined;
  count: number;
  name: string | undefined;

  taxes?: TaxItem[];
}

export interface PayoutReconciliation {
  id: string;
  performed_at: string;
  amount: number;
  payout: {
    id: string;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED';
    transferred: string | null;
    iban_last_4: string | null;
  } | null;
}

export interface BatchReceiptDetails {
  id?: string;
  residentId?: string;
  residentName?: string;
  residentFirstName?: string;
  residentLastName?: string;
  residentGender?: Gender;
  receiptImageIds?: string[] | null;
  residentActive?: boolean;
  lineItems: LineItem[];
  individualDate?: Date;
  payoutReconciliations?: PayoutReconciliation[];
}

export interface UsePreviewImageIdsReturnType {
  setPreviewImageIds: (imgIds: string[] | undefined) => void;
  enlargeImageGalleryProps: {
    initialSelectedIndex: number;
    handleClose: () => void;
    imageUrls: string[];
    imageUrlToTitleMap: Map<string, string | undefined>;
    loading: false | true;
    open: boolean;
    imageUrlToTypeMap: UrlTypeNameMapping;
  };
  setTitleForImage: (id: string, title: string | undefined) => void;
  setShownImageId: (imgId: string | undefined) => void;
}

interface ReceiptBatchFormProps<T extends ReceiptBatchFormValues> {
  initialValues: T;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  defaultInitialValues?: T;
  // the schema for the receipt batch is mostly the same except few fields, we pass this specific schema and combine it with common schema
  validationSchemaExtension: any;
  handleSubmit: (values: T, helpers: FormikHelpers<T>) => void;
  handleDeleteBatch?: VoidFunction;
  blockSubmit?: boolean;
  onChangeDirty?: (dirty: boolean) => void;
  isLoading?: boolean;
  allowNavigateAway: boolean;
  usePreviewImageIds: () => UsePreviewImageIdsReturnType;
  useRenderReceiptBatchRow: () => {
    isLoading: boolean;
    renderRow: (
      props: ServiceProviderReceiptBatchRenderRowProps
    ) => JSX.Element;
    residents?: Array<{
      id: string;
      name: string;
      firstName: string;
      lastName: string;
      gender: Gender;
    }>;
  };
  renderReceiptBatchCommonForm: (formik: FormikProps<T>) => React.ReactNode;
  renderReceiptBatchImageInput: (formik: FormikProps<T>) => React.ReactNode;
  renderCashLink?: (formik: FormikProps<T>) => React.ReactNode;
  unblockRef?: React.MutableRefObject<() => void>;
}

function ServiceProviderReceiptBatchForm<T extends ReceiptBatchFormValues>({
  initialValues,
  archivedResidents,
  defaultInitialValues,
  handleSubmit,
  handleDeleteBatch,
  validationSchemaExtension,
  blockSubmit,
  onChangeDirty,
  isLoading,
  allowNavigateAway,
  useRenderReceiptBatchRow,
  usePreviewImageIds,
  renderReceiptBatchImageInput,
  renderReceiptBatchCommonForm,
  renderCashLink,
  unblockRef,
}: ReceiptBatchFormProps<T>): React.ReactNode {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const archivedResidentIds = useMemo(() => {
    return archivedResidents?.map((r) => r.id) ?? [];
  }, [archivedResidents]);

  const [generateReceipts] = useReceiptBatchStore(
    useShallow((state) => [state.formik?.values.generateReceipts ?? false])
  );

  const selectedNursingHomePaymentProcessor =
    useSelectedNursingHomePaymentProcessor();
  const maxBatchAmountInCents = getMaxReceiptBatchAmountInCents(
    selectedNursingHomePaymentProcessor
  );

  const {
    setPreviewImageIds,
    enlargeImageGalleryProps,
    setShownImageId,
    setTitleForImage,
  } = usePreviewImageIds();

  const {
    renderRow,
    isLoading: isLoadingResidents,
    residents,
  } = useRenderReceiptBatchRow();

  const finalInitialValues = defaultInitialValues ?? initialValues;

  const internalIsLoading = Boolean(isLoading);
  const internalBlockSubmit = Boolean(blockSubmit) || isLoadingResidents;

  const getReceiptEntriesValidationSchema = useCallback(
    (individualDatesMode: boolean) =>
      Yup.array().of(
        Yup.object().shape({
          residentId: Yup.string()
            .required(t('field.select-resident'))
            .notOneOf(archivedResidentIds, t('field.resident-archived')),
          receiptImageIds: Yup.array().nullable(),
          lineItems: Yup.array().of(
            Yup.object().shape({
              amount: Yup.number()
                .required(t('receipts.batch.form.required.amount'))
                .min(1, t('field.positive-value'))
                .max(
                  MAX_TRANSACTION_AMOUNT_IN_CENTS,
                  t('field.max-value', {
                    value: fCurrencyCents(MAX_TRANSACTION_AMOUNT_IN_CENTS),
                  })
                )
                // checking self-equality works for NaN, transforming it to null
                .transform((_, val) => (val === Number(val) ? val : null)),
              name: Yup.string()
                .required(t('receipts.batch.form.required.service'))
                .max(255, t('receipts.batch.form.note-max-length')),
              ...(generateReceipts
                ? {
                    taxes: Yup.array()
                      .of(
                        Yup.object().shape({
                          taxAmount: Yup.number().min(
                            0,
                            t('field.positive-value')
                          ),
                          taxName: Yup.string().max(
                            255,
                            t('receipts.batch.form.note-max-length')
                          ),
                        })
                      )
                      .required()
                      .min(1),
                  }
                : {}),
            })
          ),
          ...(individualDatesMode
            ? {
                individualDate: Yup.date()
                  .required(t('field.select-date'))
                  .max(endOfDay(new Date()), t('field.date-past'))
                  .typeError(t('field.valid-date')),
              }
            : {}),
        })
      ),
    [archivedResidentIds, fCurrencyCents, generateReceipts, t]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        receiptDate: Yup.date().when('individualDatesMode', {
          is: (individualDatesMode: boolean) => individualDatesMode,
          then: Yup.date().notRequired(),
          otherwise: Yup.date()
            .required(t('field.select-date'))
            .max(endOfDay(new Date()), t('field.date-past'))
            .typeError(t('field.valid-date')),
        }),
        individualDatesMode: Yup.boolean(),
        receipts: Yup.array().when('individualDatesMode', {
          is: (individualDatesMode: boolean) => individualDatesMode,
          then: getReceiptEntriesValidationSchema(true),
          otherwise: getReceiptEntriesValidationSchema(false),
        }),
      }).concat(validationSchemaExtension),
    [t, validationSchemaExtension, getReceiptEntriesValidationSchema]
  );

  const [isReceiptsAmountSumOverLimit, setIsReceiptsAmountSumOverLimit] =
    useState(false);

  useEffect(() => {
    if (isReceiptsAmountSumOverLimit) {
      enqueueSnackbar(
        t('receipts.batch.form.amount-sum-over-limit', {
          maxAmount: fCurrencyCents(maxBatchAmountInCents),
        }),
        {
          variant: 'error',
          persist: true,
          preventDuplicate: true,
          key: 'receipts-batch-form-amount-sum-over-limit',
        }
      );
    } else {
      closeSnackbar('receipts-batch-form-amount-sum-over-limit');
    }
    return () => {
      closeSnackbar('receipts-batch-form-amount-sum-over-limit');
    };
  }, [
    closeSnackbar,
    enqueueSnackbar,
    fCurrencyCents,
    isReceiptsAmountSumOverLimit,
    maxBatchAmountInCents,
    t,
  ]);

  const [isFinalLoading, setIsFinalLoading] = useState(true);

  useEffect(() => {
    // The form state is synced in FormikObserver. However, this causes state to be updated
    // lazily - one render update after internalIsLoading turns false.
    // This hook is here to delay the loading state to avoid flickering
    if (!internalIsLoading) {
      setTimeout(() => {
        setIsFinalLoading(false);
      }, 0);
    }
  }, [internalIsLoading]);

  return (
    <>
      {internalIsLoading && (
        <Card
          sx={{
            p: 3,
            height: '80vh',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <LoadingContainer />
        </Card>
      )}
      <Formik
        initialValues={finalInitialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize={false}
        validateOnChange
        validateOnBlur
        validateOnMount={false}
      >
        <Stack
          sx={{ visibility: isFinalLoading ? 'hidden' : undefined }}
          data-testid={TestIds.ReceiptBatchForm}
        >
          <Stack direction="column" gap={2}>
            <SyncFormik onChangeDirty={onChangeDirty} />
            <Stack>
              <ServiceProviderReceiptBatchCommonContainer
                renderReceiptBatchCommonForm={renderReceiptBatchCommonForm}
                renderReceiptBatchImageInput={renderReceiptBatchImageInput}
                renderCashLink={renderCashLink}
              />
            </Stack>
            <ScrollToFieldError />
            <ReceiptsAmountSumLimitObserver
              setIsReceiptsAmountSumOverLimit={setIsReceiptsAmountSumOverLimit}
              maxBatchAmountInCents={maxBatchAmountInCents}
            />
            <Stack>
              <ServiceProviderReceiptBatchReceiptsView
                renderRow={renderRow}
                setShownImageId={setShownImageId}
                setPreviewImageIds={setPreviewImageIds}
                archivedResidents={archivedResidents}
                ReceiptBatchQuickResidentSelection={
                  <ServiceProviderReceiptBatchQuickResidentSelection
                    residents={residents}
                  />
                }
              />
            </Stack>
            <FormikObserver
              residents={residents}
              setPreviewImageIds={setPreviewImageIds}
              setTitleForImage={setTitleForImage}
              initialValues={finalInitialValues}
            />
            <EnlargeImageGallery {...enlargeImageGalleryProps} />
          </Stack>
          <ReceiptBatchActionButtons
            unblockRef={unblockRef}
            blockSubmit={internalBlockSubmit || isReceiptsAmountSumOverLimit}
            allowNavigateAway={allowNavigateAway}
            handleDeleteBatch={handleDeleteBatch}
          />
        </Stack>
      </Formik>
    </>
  );
}

interface ReceiptsAmountSumLimitObserverProps {
  setIsReceiptsAmountSumOverLimit: (isOverLimit: boolean) => void;
  maxBatchAmountInCents: number;
}

const ReceiptsAmountSumLimitObserver = ({
  setIsReceiptsAmountSumOverLimit,
  maxBatchAmountInCents,
}: ReceiptsAmountSumLimitObserverProps) => {
  const {
    values: { receipts },
  } = useFormikContext<ReceiptBatchFormValues>();

  const sum = useMemo(() => {
    return receipts
      .flatMap((r) => r.lineItems ?? [])
      .reduce((acc, curr) => {
        if (curr.amount === undefined) {
          return acc;
        }
        return acc + curr.amount;
      }, 0);
  }, [receipts]);

  useEffect(() => {
    setIsReceiptsAmountSumOverLimit(sum > maxBatchAmountInCents);
  }, [maxBatchAmountInCents, setIsReceiptsAmountSumOverLimit, sum]);

  return null;
};

export const getFieldErrorNames = (
  formikErrors: FormikErrors<ReceiptBatchFormValues>
): string[] => {
  const transformObjectToDotNotation = (
    obj: FormikErrors<ReceiptBatchFormValues>,
    prefix = '',
    result: string[] = []
  ) => {
    Object.keys(obj).forEach((key: string) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const value = obj[key];
      if (!value) {
        return;
      }

      const nextKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object') {
        transformObjectToDotNotation(value, nextKey, result);
      } else {
        result.push(nextKey);
      }
    });

    return result;
  };

  return transformObjectToDotNotation(formikErrors);
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ScrollToFieldErrorProps {}

export const ScrollToFieldError: FC<ScrollToFieldErrorProps> = () => {
  const { submitCount, isValid, errors } =
    useFormikContext<ReceiptBatchFormValues>();

  useEffect(() => {
    if (isValid) {
      return;
    }

    const fieldErrorNames = getFieldErrorNames(errors);
    if (fieldErrorNames.length <= 0) {
      return;
    }

    const elements = document.getElementsByName(fieldErrorNames[0]);

    if (!elements || elements.length <= 0) {
      return;
    }

    // Scroll to first known error into view
    elements[0].scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]); // Errors and isValid are not dependencies on purpose, we only want to scroll when submit count changes

  return null;
};

// Hack to synchronize state
interface SyncFormikProps {
  onChangeDirty?: (dirty: boolean) => void;
}

const SyncFormik: FC<SyncFormikProps> = ({ onChangeDirty }) => {
  const formik = useFormikContext<FormikReceiptBatchFormValues>();
  useEffect(() => {
    onChangeDirty?.(formik.dirty);
  }, [formik.dirty, onChangeDirty]);

  const setFormik = useReceiptBatchStore((state) => state.setFormik);

  useEffect(() => {
    setFormik(formik);
  }, [formik, setFormik]);

  return null;
};

interface ImageTitleFormObserverProps<T extends ReceiptBatchFormValues> {
  setPreviewImageIds: (imageIds: string[] | undefined) => void;
  setTitleForImage: (imageId: string, title: string | undefined) => void;
  residents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  initialValues?: T;
}

// A hack to allow us to access to the formik context
function FormikObserver<T extends ReceiptBatchFormValues>({
  setTitleForImage,
  residents,
  initialValues,
}: ImageTitleFormObserverProps<T>) {
  const { fCurrencyCents } = useFormatting();
  const { values, setValues, resetForm } = useFormikContext<T>();

  const receipts = values.receipts;

  const [oldInitialValues, setOldInitialValues] = useState(initialValues);

  useEffect(() => {
    if (isEqual(initialValues, oldInitialValues)) {
      return;
    }

    if (isEqual(oldInitialValues, values)) {
      resetForm({ values: initialValues });
      setOldInitialValues(initialValues);
      return;
    }

    setValues((values) => {
      const newReceipts = updateReceiptBatchWithMergeBase({
        oldServerBatch: oldInitialValues?.receipts ?? [],
        newServerBatch: initialValues?.receipts ?? [],
        localEntries: values?.receipts ?? [],
      });

      setOldInitialValues(initialValues);

      const newState = {
        ...values,
        ...initialValues,
        receipts: newReceipts,
      };

      if (isEqual(newState, initialValues)) {
        resetForm({ values: newState });
      }

      return newState;
    });
    // TODO: oldInitialValues intentionally left out?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, setValues, resetForm]);

  useMemo(() => {
    const textSeparator = ' - ';
    for (let i = 0; i < receipts.length; i++) {
      const receipt = receipts[i];

      for (const imageId of receipt.receiptImageIds ?? []) {
        const amount =
          receipt.lineItems?.reduce(
            (acc, curr) => acc + (curr.amount ?? 0),
            0
          ) ?? 0;
        const resident = receipt.residentId
          ? residents?.find((r) => r.id === receipt.residentId)
          : undefined;

        const titleElements = [`#${i + 1}`];

        if (resident) {
          // Separator between number and resident name
          titleElements.push(textSeparator);
          titleElements.push(resident.name);
        }
        if (amount !== undefined && !Number.isNaN(amount)) {
          titleElements.push(textSeparator);
          titleElements.push(fCurrencyCents(amount));
        }
        const title = titleElements.join(' ');

        setTitleForImage(imageId, title);
      }
    }
  }, [fCurrencyCents, receipts, setTitleForImage, residents]);

  return null;
}

export { ServiceProviderReceiptBatchForm };
